import { useEffect, useRef } from 'react';
import { VoiceActivityDetector } from './voice-activity-detection';

export interface UseVoiceActivityDetectionOptions {
  /** The media stream to analyze */
  stream: MediaStream | null;
  /** Audio context for analysis */
  audioContext: AudioContext | null;
  /** Optional pre-created analyser node */
  analyser?: AnalyserNode | null;
  /** Whether VAD is active */
  enabled: boolean;
  /** Volume threshold to detect silence (default: 5) */
  silenceThreshold?: number;
  /** Duration of silence before triggering (default: 1500ms) */
  silenceDuration?: number;
  /** Enable debug mode to see audio levels */
  debugMode?: boolean;
  /** Callback when speech starts */
  onSpeechStart?: () => void;
  /** Callback when speech ends */
  onSpeechEnd?: () => void;
  /** Callback when silence is detected */
  onSilenceDetected?: () => void;
  /** Callback for audio level updates (debug mode) */
  onAudioLevel?: (level: number) => void;
}

/**
 * Hook to manage Voice Activity Detection
 * Automatically sets up and tears down VAD based on stream and enabled state
 */
export const useVoiceActivityDetection = ({
  stream,
  audioContext,
  analyser,
  enabled,
  silenceThreshold = 5,
  silenceDuration = 1500,
  debugMode = false,
  onSpeechStart,
  onSpeechEnd,
  onSilenceDetected,
  onAudioLevel,
}: UseVoiceActivityDetectionOptions) => {
  const vadRef = useRef<VoiceActivityDetector | null>(null);

  // Use refs to keep callbacks stable and prevent VAD restarts
  const callbacksRef = useRef({
    onSpeechStart,
    onSpeechEnd,
    onSilenceDetected,
    onAudioLevel,
  });

  // Update refs when callbacks change without restarting VAD
  useEffect(() => {
    callbacksRef.current = {
      onSpeechStart,
      onSpeechEnd,
      onSilenceDetected,
      onAudioLevel,
    };
  }, [onSpeechStart, onSpeechEnd, onSilenceDetected, onAudioLevel]);

  useEffect(() => {
    if (!enabled || !stream || !audioContext) {
      return;
    }

    // Only set up VAD if we don't already have one
    if (vadRef.current) {
      return;
    }

    vadRef.current = new VoiceActivityDetector(
      {
        silenceThreshold,
        silenceDuration,
      },
      {
        onSpeechStart: () => {
          console.log('Speech started');
          callbacksRef.current.onSpeechStart?.();
        },
        onSpeechEnd: () => {
          console.log('Speech ended');
          callbacksRef.current.onSpeechEnd?.();
        },
        onSilenceDetected: () => {
          console.log('Silence detected');
          callbacksRef.current.onSilenceDetected?.();
        },
        onAudioLevel: debugMode ? callbacksRef.current.onAudioLevel : undefined,
      }
    );

    // Pass the analyser if provided, otherwise VAD will create its own
    vadRef.current.start(audioContext, stream, analyser || undefined);

    return () => {
      if (vadRef.current) {
        vadRef.current.stop();
        vadRef.current = null;
      }
    };
  }, [enabled, stream, audioContext, analyser, silenceThreshold, silenceDuration, debugMode]);

  return vadRef;
};
